import React, { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Fuel, MapPin, Network, Users } from "lucide-react";
import { PageHeader, PageShell } from "../components/ui/DataPage";
import { useManagedList, useStationManagementApi } from "../hooks/useStationManagement";
import GroupsTab from "../components/StationManagement/GroupsTab";
import LocationsTab from "../components/StationManagement/LocationsTab";
import StationsTab from "../components/StationManagement/StationsTab";

const TABS = [
  { key: "groups", label: "Groups", icon: Users },
  { key: "locations", label: "Locations", icon: MapPin },
  { key: "stations", label: "Charging Stations", icon: Fuel },
];

// Charging Station Management: groups -> locations -> charging stations.
// Groups are loaded once here and shared by every tab and form.
export default function StationManagement() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "stations";

  const api = useStationManagementApi();
  const fetchGroups = useCallback(() => api.listGroups(), [api]);
  const { items: groups, setItems: setGroups, isLoading: groupsLoading, error: groupsError, refetch: refetchGroups } = useManagedList(fetchGroups, [fetchGroups]);

  // a group created or edited anywhere (including inside another form) shows up everywhere at once
  const onGroupSaved = useCallback(
    (g) => {
      setGroups((list) => {
        const i = list.findIndex((x) => x.group_id === g.group_id);
        if (i === -1) return [...list, g];
        const copy = list.slice();
        copy[i] = g;
        return copy;
      });
      refetchGroups();
    },
    [setGroups, refetchGroups]
  );

  return (
    <PageShell>
      <PageHeader title="Station Management" icon={Network} description="Manage groups, their locations and the charging stations installed at each location." />

      <div role="tablist" aria-label="Station management sections" className="flex gap-1 overflow-x-auto border-b border-neutral-200">
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = key === tab;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={active}
              onClick={() => setParams({ tab: key }, { replace: true })}
              className={`-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-13 font-medium transition-colors ${
                active ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-800"
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
              {key === "groups" && groups.length > 0 && (
                <span className={`rounded-full px-1.5 text-[11px] tabular-nums ${active ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-600"}`}>{groups.length}</span>
              )}
            </button>
          );
        })}
      </div>

      {groupsError && tab !== "groups" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-13 text-amber-800">
          Groups couldn't be loaded ({groupsError}); group filters and dropdowns may be empty.{" "}
          <button className="font-medium underline" onClick={refetchGroups}>Retry</button>
        </div>
      )}

      {tab === "groups" && <GroupsTab groups={groups} isLoading={groupsLoading} error={groupsError} refetch={refetchGroups} onGroupSaved={onGroupSaved} />}
      {tab === "locations" && <LocationsTab groups={groups} groupsLoading={groupsLoading} onGroupSaved={onGroupSaved} />}
      {tab === "stations" && <StationsTab groups={groups} groupsLoading={groupsLoading} onGroupSaved={onGroupSaved} />}
    </PageShell>
  );
}
